import React from "react";

/**
 * Greyscale 2D elevation diagram for a slotted angle rack.
 * Renders front + side elevations with uprights, shelves and overall dimensions.
 *
 * Props:
 *  - cfg: object with height_in, length_in, depth_in, shelves
 *
 * Pure SVG so it scales cleanly into PDFs.
 */
export default function SlottedDiagram({ cfg }) {
  const H = Number(cfg.height_in) || 0;
  const L = Number(cfg.length_in) || 0;
  const D = Number(cfg.depth_in) || 0;
  const shelves = Math.max(2, Math.min(12, Number(cfg.shelves) || 2));
  // Stable, prop-derived pattern id so multiple diagrams on one page don't collide
  const gridId = `sa-grid-${L}-${D}-${H}-${shelves}`;
  if (H <= 0 || L <= 0 || D <= 0) return null;

  // Greyscale palette
  const C_BG = "#f5f5f4";
  const C_GRID = "#e7e5e4";
  const C_UPRIGHT = "#3f3f46"; // dark grey — slotted angles
  const C_SHELF = "#6b7280"; // mid grey — shelves
  const C_DIM = "#52525b"; // dim line / label
  const C_LABEL = "#27272a";

  // Layout — work in "diagram units" matching inches. Add margins for dim chains.
  const marginTop = 14;
  const marginBottom = 28;
  const marginLeft = 32;
  const marginRightFront = 12;
  const sideGap = 20;
  const sideW = D;

  const innerW = L + sideGap + sideW;
  const innerH = H;
  const totalW = innerW + marginLeft + marginRightFront;
  const totalH = innerH + marginTop + marginBottom;

  // Render at fixed display width
  const displayW = 520;
  const scale = displayW / totalW;
  const displayH = totalH * scale;

  const xF = marginLeft;
  const yTop = marginTop;
  const yBot = marginTop + H;

  // Upright thickness (visual only — keep proportional but not less than 0.6 in)
  const upT = Math.max(0.6, Math.min(1.4, L / 36));

  // Shelf positions (top + bottom + evenly distributed inner shelves)
  const shelfPositions = [];
  for (let i = 0; i < shelves; i++) {
    const t = i / (shelves - 1);
    shelfPositions.push(yTop + t * H);
  }
  const shelfThickness = Math.max(0.4, Math.min(0.8, H / 200));

  // Side elevation origin
  const xS = xF + L + sideGap;

  return (
    <div className="bg-white border border-line" data-testid="sa-diagram">
      <div className="px-4 py-2 flex items-center justify-between">
        <div className="text-[0.7rem] mono uppercase tracking-wider text-ink-muted">
          2D Elevation · Front + Side <span className="text-ink-faint">(grey)</span>
        </div>
        <div className="text-[0.7rem] mono text-ink-faint">
          {L}″ × {D}″ × {H}″ · {shelves} shelves
        </div>
      </div>
      <svg
        viewBox={`0 0 ${totalW} ${totalH}`}
        width={displayW}
        height={displayH}
        style={{ display: "block", margin: "0 auto", background: C_BG }}
        data-testid="sa-diagram-svg"
      >
        <pattern id={gridId} width="6" height="6" patternUnits="userSpaceOnUse">
          <path d="M 6 0 L 0 0 0 6" fill="none" stroke={C_GRID} strokeWidth="0.15" />
        </pattern>
        <rect x={xF} y={yTop} width={L} height={H} fill={`url(#${gridId})`} />
        <rect x={xS} y={yTop} width={sideW} height={H} fill={`url(#${gridId})`} />

        {/* === FRONT ELEVATION === */}
        {shelfPositions.map((y, i) => (
          <rect
            key={`fs${i}`}
            x={xF}
            y={y - shelfThickness / 2}
            width={L}
            height={shelfThickness}
            fill={C_SHELF}
          />
        ))}

        <rect x={xF} y={yTop} width={upT} height={H} fill={C_UPRIGHT} />
        <rect x={xF + L - upT} y={yTop} width={upT} height={H} fill={C_UPRIGHT} />

        {Array.from({ length: Math.floor(H / 1.2) }).map((_, i) => {
          const ty = yTop + 0.5 + i * 1.2;
          if (ty > yBot - 0.5) return null;
          return (
            <g key={`slot${i}`}>
              <line
                x1={xF + upT * 0.45}
                y1={ty}
                x2={xF + upT * 0.55}
                y2={ty + 0.5}
                stroke="#a1a1aa"
                strokeWidth="0.06"
              />
              <line
                x1={xF + L - upT * 0.55}
                y1={ty}
                x2={xF + L - upT * 0.45}
                y2={ty + 0.5}
                stroke="#a1a1aa"
                strokeWidth="0.06"
              />
            </g>
          );
        })}

        <rect x={xF} y={yTop} width={L} height={H} fill="none" stroke={C_DIM} strokeWidth="0.15" />

        {/* === SIDE ELEVATION === */}
        {shelfPositions.map((y, i) => (
          <line
            key={`ss${i}`}
            x1={xS}
            y1={y}
            x2={xS + sideW}
            y2={y}
            stroke={C_SHELF}
            strokeWidth="0.5"
          />
        ))}
        <rect x={xS} y={yTop} width={upT} height={H} fill={C_UPRIGHT} />
        <rect x={xS + sideW - upT} y={yTop} width={upT} height={H} fill={C_UPRIGHT} />
        <rect x={xS} y={yTop} width={sideW} height={H} fill="none" stroke={C_DIM} strokeWidth="0.15" />

        {/* === DIMENSION CHAINS === */}
        <DimVertical x={xF - 4} y1={yTop} y2={yBot} label={`${H}"`} color={C_DIM} textColor={C_LABEL} />
        <DimHorizontal y={yBot + 5} x1={xF} x2={xF + L} label={`${L}"`} color={C_DIM} textColor={C_LABEL} />
        <DimHorizontal y={yBot + 5} x1={xS} x2={xS + sideW} label={`${D}"`} color={C_DIM} textColor={C_LABEL} />

        <text
          x={xF + L / 2}
          y={yTop - 4}
          textAnchor="middle"
          fontFamily="monospace"
          fontSize="3"
          fill={C_LABEL}
          style={{ letterSpacing: "0.12em" }}
        >
          FRONT
        </text>
        <text
          x={xS + sideW / 2}
          y={yTop - 4}
          textAnchor="middle"
          fontFamily="monospace"
          fontSize="3"
          fill={C_LABEL}
          style={{ letterSpacing: "0.12em" }}
        >
          SIDE
        </text>
      </svg>
      <div className="px-4 py-2 border-t border-line flex flex-wrap gap-4 justify-center text-[0.65rem] text-ink-muted mono bg-surfaceAlt">
        <Legend swatch={C_UPRIGHT} label="Slotted angle (upright)" />
        <Legend swatch={C_SHELF} label="Shelf" />
      </div>
    </div>
  );
}

function Legend({ swatch, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="inline-block w-3 h-3" style={{ backgroundColor: swatch }} />
      {label}
    </span>
  );
}

function DimHorizontal({ x1, x2, y, label, color, textColor }) {
  const tick = 1.5;
  return (
    <g>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth="0.18" />
      <line x1={x1} y1={y - tick} x2={x1} y2={y + tick} stroke={color} strokeWidth="0.18" />
      <line x1={x2} y1={y - tick} x2={x2} y2={y + tick} stroke={color} strokeWidth="0.18" />
      <text
        x={(x1 + x2) / 2}
        y={y + 4.2}
        textAnchor="middle"
        fontFamily="monospace"
        fontSize="3.4"
        fill={textColor}
      >
        {label}
      </text>
    </g>
  );
}

function DimVertical({ x, y1, y2, label, color, textColor }) {
  const tick = 1.5;
  const cy = (y1 + y2) / 2;
  return (
    <g>
      <line x1={x} y1={y1} x2={x} y2={y2} stroke={color} strokeWidth="0.18" />
      <line x1={x - tick} y1={y1} x2={x + tick} y2={y1} stroke={color} strokeWidth="0.18" />
      <line x1={x - tick} y1={y2} x2={x + tick} y2={y2} stroke={color} strokeWidth="0.18" />
      <text
        x={x - 1.6}
        y={cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="monospace"
        fontSize="3.4"
        fill={textColor}
        transform={`rotate(-90 ${x - 1.6} ${cy})`}
      >
        {label}
      </text>
    </g>
  );
}
